import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { Mail, Phone, MapPin, LineChart } from "lucide-react";

export default function Footer() {
  const { t, locale } = useI18n();
  const base = `/${locale}`;

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A192F] text-[#CBD5E1]"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#059669]">
                <LineChart className="h-5 w-5 text-white" />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="font-heading text-base font-semibold tracking-tight text-white">
                  {t("brand.name")}
                </span>
                <span className="text-[11px] uppercase tracking-widest text-[#94A3B8]">
                  {t("brand.tagline")}
                </span>
              </span>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-[#94A3B8]">
              {t("footer.companyDescription")}
            </p>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-widest text-[#059669] font-mono">
              {t("footer.sectionsTitle")}
            </h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <Link
                  data-testid="footer-link-home"
                  to={base}
                  className="hover:text-white"
                >
                  {t("nav.home")}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-link-privacy"
                  to={`${base}/privacy`}
                  className="hover:text-white"
                >
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-link-cookies"
                  to={`${base}/cookies`}
                  className="hover:text-white"
                >
                  {t("footer.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  data-testid="footer-link-terms"
                  to={`${base}/terms`}
                  className="hover:text-white"
                >
                  {t("footer.terms")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-5">
            <h4 className="text-xs uppercase tracking-widest text-[#059669] font-mono">
              {t("footer.legalTitle")}
            </h4>
            <ul className="mt-5 space-y-2 text-xs leading-relaxed text-[#94A3B8]">
              <li className="text-white font-semibold text-sm">
                {t("footer.companyLegalName")}
              </li>
              <li>{t("footer.registration")}</li>
              <li>{t("footer.rcs")}</li>
              <li>{t("footer.capital")}</li>
              <li>{t("footer.vat")}</li>
              <li>{t("footer.ape")}</li>
              <li>{t("footer.license")}</li>
              <li>{t("footer.incorporation")}</li>
              <li>{t("footer.jurisdiction")}</li>
            </ul>

            <h4 className="mt-8 text-xs uppercase tracking-widest text-[#059669] font-mono">
              {t("footer.contactTitle")}
            </h4>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-[#059669]" />
                <span>{t("footer.address")}</span>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 mt-0.5 text-[#059669]" />
                <a
                  data-testid="footer-email"
                  href={`mailto:${t("footer.email")}`}
                  className="hover:text-white"
                >
                  {t("footer.email")}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 mt-0.5 text-[#059669]" />
                <a
                  data-testid="footer-phone"
                  href={`tel:${t("footer.phone").replace(/\s/g, "")}`}
                  className="hover:text-white"
                >
                  {t("footer.phone")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10">
          <p className="text-xs leading-relaxed text-[#94A3B8]">
            {t("footer.disclaimer")}
          </p>
          <p className="mt-4 text-xs text-[#64748B]">{t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
}
